import React, { useState, useRef, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
    Grid, Button, CircularProgress
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquarePen, faTrash, faCamera, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { variables } from '../Variables';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const defaultProduct = {
    productName: '',
    productSku: '',
    categoryId: '',
    subCategoryId: '',
    priceUsd: 0.0,
    priceEur: 0.0,
    priceGbp: 0.0,
    priceAud: 0.0,
    priceCad: 0.0,
    discountUsd: 0.0,
    discountEur: 0.0,
    discountGbp: 0.0,
    discountAud: 0.0,
    discountCad: 0.0,
    bulkDetail: '',
    productDescription: '',  // ✅ fixed field name
    isActive: true,
    hasVariants: false,
    productColors: []
};

const defaultFilter = {
    CategoryIds: '',
    SubCategoryIds: '',
    SizeIds: '',
    FabricIds: '',
    PageNo: 1,
    PageSize: 10,
    ProductName: '',
    OrderBy: '',     // e.g. "productName", "price", etc.
    OrderType: '',   // "asc" or "desc"
    Lang: 'en',
    Currency: 'EUR',
    ProductId: null
};

// ---------------------------------------------------------------------------
// Bulk pricing constants / helpers
// ---------------------------------------------------------------------------
const CURRENCIES = ['Usd', 'Eur', 'Gbp', 'Aud', 'Cad'];
const CURRENCY_LABELS = { Usd: 'USD', Eur: 'EUR', Gbp: 'GBP', Aud: 'AUD', Cad: 'CAD' };

const emptyTier = () => ({ minQty: '', maxQty: '', price: '', discountPrice: '' });

const defaultBulkPricing = () =>
    CURRENCIES.reduce((acc, cur) => {
        acc[cur] = { bulkEnabled: false, tiers: [emptyTier()] };
        return acc;
    }, {});

// Builds the clean JSON object that gets stringified into BulkDetail on save
const buildBulkDetailPayload = (bp) => {
    const payload = {};
    CURRENCIES.forEach((cur) => {
        if (bp[cur]?.bulkEnabled) {
            const validTiers = (bp[cur].tiers || []).filter(
                (t) => t.minQty !== '' && t.maxQty !== '' && t.price !== ''
            );
            if (validTiers.length > 0) {
                payload[CURRENCY_LABELS[cur]] = validTiers.map((t) => ({
                    minQty: Number(t.minQty),
                    maxQty: Number(t.maxQty),
                    price: Number(t.price),
                    discountPrice: t.discountPrice === '' || t.discountPrice === null
                        ? null
                        : Number(t.discountPrice)
                }));
            }
        }
    });
    return payload;
};

// Extracts numeric min/max quantities out of an old-style range label,
// e.g. "100-120 Pieces" -> { minQty: 100, maxQty: 120 }. Falls back to
// empty strings if no numeric range can be found in the label.
const parseLegacyRangeLabel = (label) => {
    const match = String(label).match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
    if (match) {
        return { minQty: Number(match[1]), maxQty: Number(match[2]) };
    }
    return { minQty: '', maxQty: '' };
};

// Hydrates the bulkPricing UI state from a stored BulkDetail string.
// Understands three shapes so existing DB data is never silently dropped:
//   1. New tier-array format:   { "USD": [{ minQty, maxQty, price, discountPrice }, ...] }
//   2. Legacy label/price map:  { "USD": { "100-120 Pieces": 100, "200-220 Pieces": 110 } }
//   3. Missing / malformed:     falls back to a blank table (never throws)
// Legacy data is auto-converted into the new tier structure here; the next
// time the product is saved via handleSave/handleUpdate it gets persisted
// back in the clean new format, so simply opening + saving a product once
// migrates it.
const parseBulkDetail = (bulkDetailString) => {
    const bp = defaultBulkPricing();
    if (!bulkDetailString) return bp;

    let parsed;
    try {
        parsed = JSON.parse(bulkDetailString);
    } catch (e) {
        // Not valid JSON at all (hand-typed junk, empty string variants, etc).
        // Start from a blank table rather than crashing the edit form.
        console.warn('Could not parse existing BulkDetail as JSON, starting fresh.', e);
        return bp;
    }

    CURRENCIES.forEach((cur) => {
        const key = CURRENCY_LABELS[cur];
        const value = parsed?.[key];
        if (!value) return;

        if (Array.isArray(value)) {
            // New format: array of tier objects
            if (value.length > 0) {
                bp[cur] = {
                    bulkEnabled: true,
                    tiers: value.map((t) => ({
                        minQty: t.minQty ?? '',
                        maxQty: t.maxQty ?? '',
                        price: t.price ?? '',
                        discountPrice: t.discountPrice ?? ''
                    }))
                };
            }
        } else if (typeof value === 'object') {
            // Legacy format: { "100-120 Pieces": 100, "200-220 Pieces": 110 }
            const entries = Object.entries(value);
            if (entries.length > 0) {
                bp[cur] = {
                    bulkEnabled: true,
                    tiers: entries.map(([label, price]) => ({
                        ...parseLegacyRangeLabel(label),
                        price: price ?? '',
                        discountPrice: ''
                    }))
                };
            }
        }
    });

    return bp;
};

const toolbarOptions = [
    ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
    ['blockquote', 'code-block'],
    ['link', 'image', 'video', 'formula'],

    [{ 'header': 1 }, { 'header': 2 }],               // custom button values
    [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
    [{ 'script': 'sub' }, { 'script': 'super' }],      // superscript/subscript
    [{ 'indent': '-1' }, { 'indent': '+1' }],          // outdent/indent
    [{ 'direction': 'rtl' }],                         // text direction

    [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

    [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
    [{ 'font': [] }],
    [{ 'align': [] }],

    ['clean']                                         // remove formatting button
];

const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'blockquote', 'code-block',
    'script',
    'list', 'bullet', 'indent',
    'link', 'image', 'video', 'formula',
    'color', 'background',
    'align', 'direction',
    'clean'
];

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 2MB
const MAX_IMAGE_COUNT = 8;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg'];

export const ProductUpload = ({ showMessage }) => {
    const user = useSelector((state) => state.user.user);
    const jsonconfig = {
        headers: {
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'application/json'
        }
    };

    const [product, setProduct] = useState({
        ...defaultProduct,
        productColors: [{ colorId: 0, colorName: "", file: null, createBy: null }],
    });
    const [productsList, setProductsList] = useState([]);
    const [errors, setErrors] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [editProductId, setEditProductId] = useState(null);
    const [filter, setFilter] = useState(defaultFilter);
    const [totalProducts, setTotalProducts] = useState(0);
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [filteredSubCategories, setFilteredSubCategories] = useState([]);
    const [validationMessages, setValidationMessages] = useState([]);
    //const bulkPricesQuillRef = useRef();
    const quillRef = useRef();
    const [uploadCount, setUploadCount] = useState(1);
    const [productColors, setProductColors] = useState([]);
    const [nextColorId, setNextColorId] = useState(1);
    const [hasVariants, setHasVariants] = useState(false);

    // Structured bulk-pricing UI state (replaces the free-text BulkDetail textarea)
    const [bulkPricing, setBulkPricing] = useState(defaultBulkPricing());

    const columns = [
        { field: 'productId', headerName: 'ID', width: 50 },
        { field: 'productName', headerName: 'Name', width: 200 },
        { field: 'productSku', headerName: 'SKU', width: 100, disableColumnMenu: true },
        { field: 'categoryId', headerName: 'Category ID', width: 120 },
        { field: 'subCategoryId', headerName: 'SubCategory ID', width: 120 },
        { field: 'isActive', headerName: 'Visibility', width: 100, disableColumnMenu: true },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 180,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <>
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(params.row.productId)}
                        style={{ marginRight: 8 }}
                    >
                        Delete
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                            setEditMode(true);
                            setEditProductId(params.row.productId);
                            setHasVariants(params.row?.hasVariants ?? false);
                            const sortedColors = [...params.row.productColors].sort((a, b) => a.colorId - b.colorId);
                            const lastColorId = sortedColors[sortedColors.length - 1]?.colorId;
                            setProduct({
                                ...params.row,
                                productColors: sortedColors.map((color, index) => ({
                                    ...color,
                                    colorId: color.colorId // or use index or generate new ID here if needed
                                }))
                            });
                            setNextColorId(lastColorId + 1);
                            // Hydrate the bulk pricing table from the stored BulkDetail JSON
                            setBulkPricing(parseBulkDetail(params.row.bulkDetail));
                            //setProduct(params.row);
                        }}
                    >
                        Edit
                    </Button>
                </>
            )
        }
    ];

    useEffect(() => {
        loadProducts();
    }, [filter]);

    useEffect(() => {
        loadCategories();
        loadSubCategories();
    }, []);

    useEffect(() => {
        if (product?.productColors?.length === 0 && !editMode) {
            setProduct(prev => ({
                ...prev,
                productColors: [{ colorId: 0, colorName: '', file: null, createBy: null }]
            }));
        }
    }, [product?.productColors?.length, editMode]);

    const handleDescriptionImageUpload = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (file) {
                const formData = new FormData();
                const name = `Description${uploadCount}`;
                formData.append('image', file);
                formData.append('name', name);

                try {
                    const res = await axios.post(
                        `${variables.API_URL}Product/UploadImage`,
                        formData,
                        {
                            headers: {
                                Authorization: `Bearer ${user.token}`
                            }
                        }
                    );

                    if (res.data && res.data.imageUrl) {
                        const imageUrl = res.data.imageUrl;

                        // Get Quill instance and insert the image
                        const editor = quillRef.current.getEditor();
                        const range = editor.getSelection(true);
                        editor.insertEmbed(range.index, 'image', imageUrl);

                        setUploadCount(prev => prev + 1);
                    } else {
                        console.error('Image upload failed.');
                    }
                } catch (error) {
                    console.error('Image upload error:', error);
                }
            }
        };
    };

    useEffect(() => {
        // Adding handler after Quill editor is initialized
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            quill.getModule('toolbar').addHandler('image', handleDescriptionImageUpload);
        }
    }, []);

    const modules = {
        toolbar: {
            container: toolbarOptions
        }
    };

    const loadProducts = async () => {
        setLoading(true);
        const res = await axios.post(
            variables.API_URL + 'Product/GetFilterProductsForAdmin',
            filter,
            jsonconfig
        );
        setProductsList(res.data.products);
        setTotalProducts(res.data.totalCount);
        setLoading(false);
    };

    const loadCategories = async () => {
        const res = await axios.get(variables.API_URL + 'ClothingCategory/GetAllCategories', jsonconfig);
        setCategories(res.data);
    };

    const loadSubCategories = async () => {
        const res = await axios.get(variables.API_URL + 'ClothingSubcategory/GetAllSubCategories', jsonconfig);
        setSubCategories(res.data);
        setFilteredSubCategories(res.data);
    };

    // -----------------------------------------------------------------------
    // Bulk pricing table handlers
    // -----------------------------------------------------------------------
    const toggleBulkEnabled = (cur) => {
        setBulkPricing((prev) => ({
            ...prev,
            [cur]: { ...prev[cur], bulkEnabled: !prev[cur].bulkEnabled }
        }));
    };

    const updateTierField = (cur, index, field, value) => {
        setBulkPricing((prev) => {
            const tiers = [...prev[cur].tiers];
            tiers[index] = { ...tiers[index], [field]: value };
            return { ...prev, [cur]: { ...prev[cur], tiers } };
        });
        resetFieldStyle(`bulk${cur}${index}`);
    };

    const addTier = (cur) => {
        setBulkPricing((prev) => ({
            ...prev,
            [cur]: { ...prev[cur], tiers: [...prev[cur].tiers, emptyTier()] }
        }));
    };

    const removeTier = (cur, index) => {
        setBulkPricing((prev) => {
            const tiers = prev[cur].tiers.filter((_, i) => i !== index);
            return { ...prev, [cur]: { ...prev[cur], tiers: tiers.length ? tiers : [emptyTier()] } };
        });
    };

    const validate = () => {
        const messages = [];
        const newErrors = {};

        // Name
        if (!product.productName?.trim()) {
            messages.push("Product name is required.");
            newErrors.productName = true;
        }

        // SKU
        if (!product.productSku?.trim()) {
            messages.push("Product SKU is required.");
            newErrors.productSku = true;
        }

        // Category
        if (!product.categoryId) {
            messages.push("Category is required.");
            newErrors.categoryId = true;
        }

        // subCategory
        if (!product.subCategoryId && product.categoryId != 3) {
            messages.push("subCategory is required.");
            newErrors.subCategoryId = true;
        }

        // Price Fields
        const prices = {
            Usd: product.priceUsd,
            Eur: product.priceEur,
            Gbp: product.priceGbp,
            Aud: product.priceAud,
            Cad: product.priceCad
        };

        //for (const [currency, value] of Object.entries(prices)) {
        //    if (!value && value < 0) {
        //        messages.push(`Price in ${currency.toUpperCase()} is required`);
        //        newErrors[`price${currency}`] = true;
        //    }
        //}

        // Discount Fields
        const discounts = {
            Usd: product.discountUsd,
            Eur: product.discountEur,
            Gbp: product.discountGbp,
            Aud: product.discountAud,
            Cad: product.discountCad
        };

        const hasAnyDiscount = Object.values(discounts).some(val => !!val);

        if (hasAnyDiscount) {
            for (const [currency, value] of Object.entries(discounts)) {
                if (!value) {
                    messages.push(`Discount in ${currency.toUpperCase()} is required.`);
                    newErrors[`discount${currency}`] = true;
                }
            }
        }

        // Images - Required, Max count, Type, Size
        if (product.productColors.length === 0) {
            messages.push("At least one product color and image is required.");
            newErrors.productColors = true;
        } else {
            product.productColors.forEach((color, index) => {
                if (!color.colorName?.trim()) {
                    messages.push(`Color name is required for row ${index + 1}.`);
                    newErrors[`colorName${index}`] = true;
                }

                const hasNewFile = !!color.file;
                const hasExistingImage = !!color.productImage;

                if (!hasNewFile && !hasExistingImage) {
                    messages.push(`Image is required for row ${index + 1}.`);
                    newErrors[`colorImage${index}`] = true;
                } else if (hasNewFile) {
                    if (!ALLOWED_TYPES.includes(color.file.type)) {
                        messages.push(`Image in row ${index + 1} must be JPEG, PNG, or GIF.`);
                        newErrors[`colorImage${index}`] = true;
                    }
                    if (color.file.size > MAX_IMAGE_SIZE) {
                        messages.push(`Image in row ${index + 1} must be less than or equal to 2MB.`);
                        newErrors[`colorImage${index}`] = true;
                    }
                }
            });
        }

        // Bulk pricing - validate only the currencies where bulk pricing is enabled
        CURRENCIES.forEach((cur) => {
            if (bulkPricing[cur].bulkEnabled) {
                bulkPricing[cur].tiers.forEach((tier, idx) => {
                    if (tier.minQty === '' || tier.maxQty === '' || tier.price === '') {
                        messages.push(`Complete Min Qty, Max Qty and Price for ${CURRENCY_LABELS[cur]} bulk tier ${idx + 1}, or remove it.`);
                        newErrors[`bulk${cur}${idx}`] = true;
                    } else if (Number(tier.minQty) > Number(tier.maxQty)) {
                        messages.push(`Min Qty cannot be greater than Max Qty for ${CURRENCY_LABELS[cur]} bulk tier ${idx + 1}.`);
                        newErrors[`bulk${cur}${idx}`] = true;
                    }
                });
            }
        });

        // Description
        if (!product.productDescription?.trim()) {
            messages.push("Product description is required.");
            newErrors.productDescription = true;
        }

        setValidationMessages(messages);
        setErrors(newErrors);

        return messages.length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setLoading(true);

        try {
            const formData = new FormData();
            const bulkDetailPayload = JSON.stringify(buildBulkDetailPayload(bulkPricing));

            // Append basic product data (excluding ProductColors)
            formData.append('ProductName', product.productName);
            formData.append('ProductSku', product.productSku);
            formData.append('CategoryId', product.categoryId);
            if (product.subCategoryId) {
                formData.append('SubCategoryId', product.subCategoryId);
            }
            formData.append('PriceUsd', product.priceUsd);
            formData.append('PriceEur', product.priceEur);
            formData.append('PriceGbp', product.priceGbp);
            formData.append('PriceAud', product.priceAud);
            formData.append('PriceCad', product.priceCad);
            formData.append('DiscountUsd', product.discountUsd);
            formData.append('DiscountEur', product.discountEur);
            formData.append('DiscountGbp', product.discountGbp);
            formData.append('DiscountAud', product.discountAud);
            formData.append('DiscountCad', product.discountCad);
            formData.append('ProductDescription', product.productDescription);
            formData.append('BulkDetail', bulkDetailPayload);
            formData.append('CreatedBy', user.user.userId);
            formData.append('HasVariants', hasVariants);

            // Append each productColor
            product.productColors.forEach((color, index) => {
                formData.append(`ProductColors[${index}].ColorName`, color.colorName);
                formData.append(`ProductColors[${index}].File`, color.file);
                formData.append(`ProductColors[${index}].CreateBy`, user.user.userId);
            });

            const response = await axios.post(
                variables.API_URL + 'Product/CreateProduct',
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            const resData = response.data;
            if (resData.success || resData.includes("saved successfully")) {
                showMessage("success", "Product saved successfully!");
                resetForm();
                loadProducts();
            } else {
                showMessage("error", "Something went wrong: " + resData);
            }

        } catch (error) {
            if (error.response?.data) {
                showMessage("error", error.response.data);
            } else {
                showMessage("error", "An unexpected error occurred: " + error.message);
            }
        } finally {
            setLoading(false);
        }
    };


    const handleUpdate = async () => {
        if (!validate()) return;
        setLoading(true);

        try {
            const formData = new FormData();
            const bulkDetailPayload = JSON.stringify(buildBulkDetailPayload(bulkPricing));

            // Append basic product data (excluding ProductColors)
            formData.append('ProductId', product.productId);
            formData.append('ProductName', product.productName);
            formData.append('ProductSku', product.productSku);
            formData.append('CategoryId', product.categoryId);
            if (product.subCategoryId) {
                formData.append('SubCategoryId', product.subCategoryId);
            }
            formData.append('PriceUsd', product.priceUsd);
            formData.append('PriceEur', product.priceEur);
            formData.append('PriceGbp', product.priceGbp);
            formData.append('PriceAud', product.priceAud);
            formData.append('PriceCad', product.priceCad);
            formData.append('DiscountUsd', product.discountUsd == null ? 0.0 : product.discountUsd);
            formData.append('DiscountEur', product.discountEur == null ? 0.0 : product.discountEur);
            formData.append('DiscountGbp', product.discountGbp == null ? 0.0 : product.discountGbp);
            formData.append('DiscountAud', product.discountAud == null ? 0.0 : product.discountAud);
            formData.append('DiscountCad', product.discountCad == null ? 0.0 : product.discountCad);
            formData.append('ProductDescription', product.productDescription);
            formData.append('BulkDetail', bulkDetailPayload);
            formData.append('CreatedBy', user.user.userId);
            formData.append('HasVariants', hasVariants);

            // Append each productColor
            product.productColors.forEach((color, index) => {
                formData.append(`ProductColors[${index}].ColorId`, color.colorId);
                formData.append(`ProductColors[${index}].ColorName`, color.colorName);
                formData.append(`ProductColors[${index}].CreateBy`, user.user.userId);
                if (color.productImage) {
                    formData.append(`ProductColors[${index}].ProductImage`, color.productImage);
                } else {
                    formData.append(`ProductColors[${index}].File`, color.file);
                }
            });

            const response = await axios.post(
                variables.API_URL + 'Product/UpdateProduct',
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            // Assuming backend returns a message in response.data.message or just response.data
            const resData = response.data;

            if (resData.includes("updated successfully")) {
                showMessage("success", "Product Updated successfully!");
                resetForm();
                loadProducts();
            } else {
                showMessage("error", "Something went wrong: " + resData);
            }

        } catch (error) {
            // Handle error (network issues, server crash, etc.)
            if (error.response?.data) {
                showMessage("error", error.response.data);
            } else {
                showMessage("error", "An unexpected error occurred: " + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${variables.API_URL}Product/DeleteProductById?id=${id}`,
                {}, // POST body is empty; ID is passed as query param
                jsonconfig
            );

            const message = response?.data;
            const lowerMessage = message.toLowerCase();

            if (
                lowerMessage.includes("error") ||
                lowerMessage.includes("not found") ||
                lowerMessage.includes("fail") ||
                lowerMessage.includes("exception")
            ) {
                showMessage("error", message);
            } else {
                showMessage("success", message);
                loadProducts(); // refresh list on success
            }
        } catch (error) {
            const errorMsg = error.response?.data || "Something went wrong while deactivating the product.";
            showMessage("error", errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setProduct({
            ...defaultProduct,
            productColors: [{ colorId: 0, colorName: "", file: null, createBy: null }],
        });
        setErrors({});
        setValidationMessages([]);
        setHasVariants(false);
        setEditMode(false);
        setEditProductId(null);
        setBulkPricing(defaultBulkPricing());

        // ✅ reset editor content
        if (quillRef.current) {
            const editor = quillRef.current.getEditor();
            editor.setContents([]); // clear HTML content
        }
    };

    const handleImageUpload = (e, colorId) => {
        const file = e.target.files[0]; // only one file per color

        setProduct(prev => {
            const updatedColors = prev.productColors.map(color => {
                // Use either 'id' or 'colorId' for matching
                const matchId = color.colorId;

                if (matchId === colorId) {
                    return {
                        ...color,
                        file,                      // set file for preview
                        productImage: null         // clear URL if it's edit mode
                    };
                }
                return color;
            });

            return {
                ...prev,
                productColors: updatedColors
            };
        });
    };

    const handlerAddMoreImages = (e) => {
        setProduct(prev => ({
            ...prev,
            productColors: [
                ...prev.productColors,
                { colorId: nextColorId, colorName: '', file: null, createBy: null }
            ]
        }));
        setNextColorId(prev => prev + 1);
    }

    const handlerDeleteImageRow = (id) => {
        setProduct(prev => ({
            ...prev,
            productColors: prev.productColors.filter(color => color.colorId !== id)
        }));
    }

    const deleteImageOnEdit = (id) => {
        setProduct(prev => ({
            ...prev,
            productColors: prev.productColors.filter(color => color.colorId !== id)
        }));
    }

    const resetFieldStyle = (fieldName) => {
        let _errors = errors;
        if (product[fieldName] && errors[fieldName]) {
            setErrors(prev => ({ ...prev, [fieldName]: false }));
        }
        else if (fieldName == "images" && errors[fieldName]) {
            setErrors(prev => ({ ...prev, [fieldName]: false }));
        }
        else if (errors[fieldName]) {
            // Covers dynamically-named error keys that don't map to a `product` field,
            // e.g. bulk pricing tier errors (bulk{Currency}{index}).
            setErrors(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    const isQuillContentEmpty = (html) => {
        const div = document.createElement("div");
        div.innerHTML = html;
        return div.innerText.trim() === ""; // checks visible text only
    };

    const handleFilterChange = (filterModel) => {
        const newFilter = {
            ...filter,
            CategoryIds: '',
            SubCategoryIds: '',
            SizeIds: '',
            FabricIds: '',
            ProductName: '',
            CreatedOn: null,
            UpdatedOn: null,
            ProductId: null,
            PageNo: 1 // Reset to first page on filter
        };

        const activeFilters = filterModel.items.filter(
            (item) => item.value !== undefined && item.value !== null && item.value !== ''
        );

        activeFilters.forEach(({ field, value }) => {
            switch (field) {
                case 'categoryId':
                    newFilter.CategoryIds = value.toString();
                    break;
                case 'subCategoryId':
                    newFilter.SubCategoryIds = value.toString();
                    break;
                case 'sizeId':
                    newFilter.SizeIds = value.toString();
                    break;
                case 'fabricIds':
                    newFilter.FabricIds = value.toString();
                    break;
                case 'productName':
                    newFilter.ProductName = value.trim?.() || value;
                    break;
                case 'productId':
                    newFilter.ProductId = parseInt(value);
                    break;
                case 'createdOn':
                    newFilter.CreatedOn = value;
                    break;
                case 'updatedOn':
                    newFilter.UpdatedOn = value;
                    break;
                // add more if needed
            }
        });

        // Optional: prevent re-render loop
        if (JSON.stringify(filter) !== JSON.stringify(newFilter)) {
            setFilter(newFilter);
        }
    };

    return (
        <>
            <div className="row">
                <div className="col-sm-12 px-4">
                    <div className="form-container mb-3 text-end">
                        {validationMessages.length > 0 && (
                            <div className="alert alert-danger">
                                <ul className="text-start list-style-none mb-0">
                                    {validationMessages.map((msg, idx) => (
                                        <li key={idx}>{idx + 1}. {msg}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <form onSubmit={(e) => { e.preventDefault(); editMode ? handleUpdate() : handleSave(); }}>
                            <div className="row">
                                <label className="col-sm-4 col-lg-2 form-group-label mb-3">Product Name</label>
                                <div className="col-sm-8 col-lg-4 mb-3">
                                    <input
                                        type="text"
                                        id="name"
                                        value={product.productName}
                                        className={`form-control ${errors.productName ? 'border-red' : ''}`}
                                        onChange={(e) => {
                                            setProduct({ ...product, productName: e.target.value });
                                            resetFieldStyle("productName");
                                        }}
                                    />
                                </div>

                                <label className="col-sm-4 col-lg-2 form-group-label mb-3">SKU</label>
                                <div className="col-sm-8 col-lg-4 mb-3">
                                    <input
                                        type="text"
                                        id="productSku"
                                        value={product.productSku}
                                        className={`form-control ${errors.productSku ? 'border-red' : ''}`}
                                        onChange={(e) => {
                                            setProduct({ ...product, productSku: e.target.value })
                                            resetFieldStyle("productSku");
                                        }}
                                    />
                                </div>

                                <label className="col-sm-4 col-lg-2 form-group-label mb-3">Category</label>
                                <div className="col-sm-8 col-lg-4 mb-3">
                                    <select
                                        id="categoryId"
                                        value={product.categoryId}
                                        className={`form-select ${errors.categoryId ? 'border-red' : ''}`}
                                        onChange={(e) => {
                                            setProduct({ ...product, categoryId: e.target.value });
                                            if (subCategories.length > 0) {
                                                let _subCategories = subCategories.filter(sc => {
                                                    return sc.categoryId == e.target.value
                                                })
                                                setFilteredSubCategories(_subCategories);
                                            }
                                            resetFieldStyle("categoryId");
                                        }}
                                    >
                                        <option value="">Select a category</option>
                                        {
                                            categories.map(cat => {
                                                return (
                                                    <option key={cat.categoryId} value={cat.categoryId}>{cat.categoryName}</option>
                                                )
                                            })
                                        }
                                    </select>
                                </div>

                                <label className="col-sm-4 col-lg-2 form-group-label mb-3">Subcategory</label>
                                <div className="col-sm-8 col-lg-4 mb-3">
                                    {
                                        product.categoryId ?

                                            <select
                                                id="subCategoryId"
                                                value={product.subCategoryId}
                                                className={`form-select ${errors.subCategoryId ? 'border-red' : ''}`}
                                                onChange={(e) => {
                                                    setProduct({ ...product, subCategoryId: e.target.value });
                                                    resetFieldStyle("subCategoryId");
                                                }}
                                            ><option value="">Select Subcategory</option>
                                                {
                                                    product.categoryId && filteredSubCategories.length > 0 ? filteredSubCategories.map(sub => {
                                                        return (
                                                            <option key={sub.subCategoryId} value={sub.subCategoryId}>{sub.subCategoryName}</option>
                                                        )
                                                    })
                                                        :
                                                        <></>
                                                }
                                            </select>
                                            :

                                            <select
                                                id="subCategoryId"
                                                value={product.subCategoryId}
                                                className={`form-select ${errors.subCategoryId ? 'border-red' : ''}`}
                                                onChange={(e) => {
                                                    setProduct({ ...product, subCategoryId: e.target.value });
                                                    resetFieldStyle("subCategoryId");
                                                }}
                                                disabled
                                            ><option value="">Select Subcategory</option>
                                                {
                                                    product.categoryId && filteredSubCategories.length > 0 ? filteredSubCategories.map(sub => {
                                                        return (
                                                            <option key={sub.subCategoryId} value={sub.subCategoryId}>{sub.subCategoryName}</option>
                                                        )
                                                    })
                                                        :
                                                        <></>
                                                }
                                            </select>
                                    }

                                </div>
                                <label className="col-sm-4 col-lg-2 form-group-label mb-3" />
                                <div className="col-sm-8 col-lg-10 mb-3 text-start">
                                    <label className="cursor-pointer form-group-label mb-0">
                                        <input
                                            type="checkbox"
                                            className="me-1"
                                            checked={hasVariants}
                                            onChange={() => setHasVariants((prev) => !prev)}
                                        />
                                        Has Variants (like Full Set (Top + Bottom), Top Only, Bottom Only)
                                    </label>
                                </div>

                                {/* ------------------------------------------------------------------ */}
                                {/* Pricing table: Price / Discounted Price / Bulk pricing per currency */}
                                {/* Replaces the previous separate Price and Discount field loops and   */}
                                {/* the free-text Bulk Prices textarea.                                 */}
                                {/* ------------------------------------------------------------------ */}
                                <label className="col-sm-4 col-lg-2 form-group-label align-items-start mb-3">Pricing</label>
                                <div className="col-sm-8 col-lg-10 mb-3">
                                    <table className="table table-bordered align-middle mb-0">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '90px' }}>Currency</th>
                                                <th>Price</th>
                                                <th>Discounted Price</th>
                                                <th style={{ width: '160px' }}>Bulk Pricing</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {CURRENCIES.map((currency) => (
                                                <React.Fragment key={currency}>
                                                    <tr>
                                                        <td className="fw-bold">{CURRENCY_LABELS[currency]}</td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                id={`price${currency}`}
                                                                className={`form-control ${errors[`price${currency}`] ? 'border-red' : ''}`}
                                                                value={product[`price${currency}`]}
                                                                onChange={(e) => {
                                                                    setProduct({ ...product, [`price${currency}`]: e.target.value });
                                                                    resetFieldStyle(`price${currency}`);
                                                                }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                id={`discount${currency}`}
                                                                className={`form-control ${errors[`discount${currency}`] ? 'border-red' : ''}`}
                                                                value={product[`discount${currency}`]}
                                                                onChange={(e) => {
                                                                    setProduct({ ...product, [`discount${currency}`]: e.target.value });
                                                                    resetFieldStyle(`discount${currency}`);
                                                                }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <label className="cursor-pointer form-group-label mb-0">
                                                                <input
                                                                    type="checkbox"
                                                                    className="me-1"
                                                                    checked={bulkPricing[currency].bulkEnabled}
                                                                    onChange={() => toggleBulkEnabled(currency)}
                                                                />
                                                                Enable bulk pricing
                                                            </label>
                                                        </td>
                                                    </tr>

                                                    {bulkPricing[currency].bulkEnabled && (
                                                        <tr>
                                                            <td colSpan={4} className="bg-light">
                                                                <table className="table table-sm mb-0">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Min Qty</th>
                                                                            <th>Max Qty</th>
                                                                            <th>Price</th>
                                                                            <th>Discounted Price</th>
                                                                            <th style={{ width: '80px' }}></th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {bulkPricing[currency].tiers.map((tier, idx) => (
                                                                            <tr key={idx}>
                                                                                <td>
                                                                                    <input
                                                                                        type="number"
                                                                                        className={`form-control ${errors[`bulk${currency}${idx}`] ? 'border-red' : ''}`}
                                                                                        value={tier.minQty}
                                                                                        onChange={(e) => updateTierField(currency, idx, 'minQty', e.target.value)}
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    <input
                                                                                        type="number"
                                                                                        className={`form-control ${errors[`bulk${currency}${idx}`] ? 'border-red' : ''}`}
                                                                                        value={tier.maxQty}
                                                                                        onChange={(e) => updateTierField(currency, idx, 'maxQty', e.target.value)}
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    <input
                                                                                        type="number"
                                                                                        className={`form-control ${errors[`bulk${currency}${idx}`] ? 'border-red' : ''}`}
                                                                                        value={tier.price}
                                                                                        onChange={(e) => updateTierField(currency, idx, 'price', e.target.value)}
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    <input
                                                                                        type="number"
                                                                                        className="form-control"
                                                                                        value={tier.discountPrice}
                                                                                        onChange={(e) => updateTierField(currency, idx, 'discountPrice', e.target.value)}
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    {idx === bulkPricing[currency].tiers.length - 1 ? (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="site-btn"
                                                                                            title="add bulk tier"
                                                                                            onClick={() => addTier(currency)}
                                                                                        >
                                                                                            <FontAwesomeIcon icon={faPlus} />
                                                                                        </button>
                                                                                    ) : (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="site-btn"
                                                                                            title="remove bulk tier"
                                                                                            onClick={() => removeTier(currency, idx)}
                                                                                        >
                                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                                        </button>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <label className={`col-sm-4 col-lg-2 form-group-label mb-3`}>Upload Images</label>
                                {
                                    product && product?.productColors.length > 0 ? product.productColors.map((item, i) => {
                                        var imgBasePath = variables.projectImgPath;
                                        return (
                                            <>
                                                {
                                                    i > 0 ?
                                                        <label className="col-sm-4 col-lg-2 form-group-label mb-3"></label>
                                                        :
                                                        <></>
                                                }
                                                <div className="d-flex col-sm-8 col-lg-10 mb-3">
                                                    <input type="text"
                                                        className={`form-control me-3 ${errors["color" + item.colorName] ? 'border-red' : ''}`}
                                                        value={item.colorName}
                                                        placeholder="Color name"
                                                        onChange={(e) => {
                                                            const updatedColors = [...product.productColors];
                                                            const colorIndex = updatedColors.findIndex(c => c.colorId === item.colorId);
                                                            if (colorIndex !== -1) {
                                                                updatedColors[colorIndex].colorName = e.target.value;
                                                                setProduct(prev => ({
                                                                    ...prev,
                                                                    productColors: updatedColors
                                                                }));
                                                            }
                                                        }}
                                                    />
                                                    <input
                                                        type="file"
                                                        multiple
                                                        accept="image/*"
                                                        id={`images${item.colorId}`}
                                                        className={`form-control me-3 choose-file ${errors["color" + item.colorId] ? 'border-red' : ''}`}
                                                        onChange={(e) => {
                                                            handleImageUpload(e, item.colorId);
                                                            resetFieldStyle(`images${item.colorId}`);
                                                        }}
                                                    />
                                                    {(item.file || item.productImage) && (
                                                        <img
                                                            src={item.file ? URL.createObjectURL(item.file) : imgBasePath + item.productImage}
                                                            alt="Preview"
                                                            style={{ width: '100px', marginRight: '1rem', height: "52px" }}
                                                        />
                                                    )}
                                                    {
                                                        i == 0 ?

                                                            <button type="button" className="site-btn" title="add image" onClick={handlerAddMoreImages}>
                                                                <FontAwesomeIcon icon={faPlus} />
                                                            </button>
                                                            :
                                                            <button type="button" className="site-btn" title="add image" onClick={(e) => handlerDeleteImageRow(item.colorId)}>
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                    }
                                                </div>
                                            </>
                                        )
                                    })
                                        : <></>
                                }
                                <label className="col-sm-4 col-lg-2 form-group-label align-items-start mb-3">Description</label>
                                <div className="col-sm-8 col-lg-10 mb-3">
                                    <ReactQuill
                                        ref={quillRef}
                                        value={product.productDescription}
                                        onChange={(content, delta, source, editor) => {
                                            const cleanContent = isQuillContentEmpty(content) ? "" : content;
                                            setProduct(prev => ({ ...prev, productDescription: cleanContent }))
                                            resetFieldStyle("description");
                                        }}
                                        modules={modules}
                                        formats={formats}
                                        placeholder="Type product description here..."
                                        className={`${errors.productDescription ? "border-red border-r10" : ""}`}
                                    />

                                </div>
                                <div className="col-12 text-end">
                                    <button type="submit" className="site-btn">
                                        {loading ? 'Saving...' : editMode ? 'Update Product' : 'Upload Product'}
                                    </button>
                                    {editMode && (
                                        <button type="button" className="site-btn ms-2" onClick={resetForm}>Cancel Edit</button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12 px-4">
                    <span className="form-title">Products List</span>
                    <div className="form-container mb-3" style={{ height: 500, width: '100%' }}>
                        {loading ? (
                            <CircularProgress />
                        ) : (
                            <DataGrid
                                rows={productsList}
                                columns={columns}
                                getRowId={(row) => row.productId}
                                rowCount={totalProducts}
                                loading={loading}
                                paginationMode="server"
                                sortingMode="server"
                                filterMode="server"
                                onFilterModelChange={handleFilterChange}
                                paginationModel={{
                                    page: filter.PageNo - 1,
                                    pageSize: filter.PageSize,
                                }}
                                onPaginationModelChange={({ page, pageSize }) => {
                                    setFilter(prev => ({
                                        ...prev,
                                        PageNo: page + 1,
                                        PageSize: pageSize,
                                    }));
                                }}
                                sortModel={
                                    filter.OrderBy && filter.OrderType
                                        ? [{ field: filter.OrderBy, sort: filter.OrderType }]
                                        : []
                                }
                                onSortModelChange={(sortModel) => {
                                    if (sortModel.length > 0 && (sortModel[0].sort === 'asc' || sortModel[0].sort === 'desc')) {
                                        const { field, sort } = sortModel[0];
                                        setFilter(prev => ({
                                            ...prev,
                                            OrderBy: field,
                                            OrderType: sort
                                        }));
                                    } else {
                                        // Clear sort only if model is empty (not invalid)
                                        setFilter(prev => ({
                                            ...prev,
                                            OrderBy: '',
                                            OrderType: ''
                                        }));
                                    }
                                }}
                                pageSizeOptions={[10, 20, 50]}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}